import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import ImageUploader from "@/components/ImageUploader";
import DimSearchEditor from "@/components/admin/DimSearchEditor";
import {
    Plus,
    Pencil,
    Trash2,
    Check,
    X,
    Layers,
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    Ruler,
} from "lucide-react";

// ---------- Categories Tab ----------
export default function CategoriesTab() {
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [confirm, setConfirm] = useState(null);
    const [dragIdx, setDragIdx] = useState(null);
    const [dimCat, setDimCat] = useState(null);

    const load = async () => {
        const { data } = await api.get("/categories");
        setCategories(data);
    };

    useEffect(() => { load(); }, []);

    const persistOrder = async (list) => {
        setCategories(list.map((c, i) => ({ ...c, order: i + 1 })));
        try {
            await api.put("/admin/categories/reorder", { ids: list.map((c) => c.id) });
        } catch (e) {
            setError(e.response?.data?.detail || "Reorder failed");
            load();
        }
    };

    const move = (from, to) => {
        if (to < 0 || to >= categories.length || from === to) return;
        const list = [...categories];
        const [item] = list.splice(from, 1);
        list.splice(to, 0, item);
        persistOrder(list);
    };

    const save = async () => {
        setError("");
        try {
            const payload = {
                name: editing.name,
                image_url: editing.image_url || null,
                order: parseInt(editing.order, 10) || 0,
            };
            if (editing.id) {
                await api.put(`/categories/${editing.id}`, payload);
            } else {
                await api.post("/categories", payload);
            }
            setEditing(null);
            load();
        } catch (e) {
            setError(e.response?.data?.detail || "Save failed");
        }
    };

    const askRemove = (c) => {
        setConfirm({
            title: "Delete category",
            message: `Delete "${c.name}" AND its ${c.product_count} products? This cannot be undone.`,
            onConfirm: async () => {
                await api.delete(`/categories/${c.id}`);
                setConfirm(null);
                load();
            },
        });
    };

    return (
        <div data-testid="admin-categories-tab">
            {dimCat && <DimSearchEditor category={dimCat} onClose={() => setDimCat(null)} onSaved={() => { setDimCat(null); load(); }} />}
            <div className="flex items-center justify-between mb-4">
                <div className="font-mono text-sm text-slate-500">
                    {categories.length} categories · <span className="text-slate-400">tërhiq kartat ose përdor shigjetat për renditjen në faqen kryesore</span>
                </div>
                <button onClick={() => setEditing({ name: "", image_url: "", order: categories.length + 1 })} className="btn-primary" data-testid="new-category-button">
                    <Plus className="w-4 h-4" /> New category
                </button>
            </div>

            {error && !editing && <div className="text-sm text-red-700 mb-3" data-testid="category-reorder-error">{error}</div>}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {categories.map((c, idx) => (
                    <div
                        key={c.id}
                        draggable
                        onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => { if (dragIdx !== null) move(dragIdx, idx); setDragIdx(null); }}
                        onDragEnd={() => setDragIdx(null)}
                        className={`border border-slate-200 group cursor-move transition-opacity ${dragIdx === idx ? "opacity-40" : ""}`}
                        data-testid={`category-row-${c.id}`}
                    >
                        <div className="aspect-square bg-slate-50 overflow-hidden">
                            {c.image_url ? (
                                <img src={c.image_url} alt={c.name} className="w-full h-full object-contain p-3" onError={(e) => (e.currentTarget.style.display = "none")} />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-300"><Layers className="w-12 h-12" /></div>
                            )}
                        </div>
                        <div className="p-2 border-t border-slate-200">
                            <div className="font-display font-bold text-xs uppercase truncate">
                                {c.order > 0 && <span className="mr-1 text-slate-400 font-mono">#{String(c.order).padStart(2, "0")}</span>}
                                {c.name}
                            </div>
                            <div className="flex justify-between items-center mt-1">
                                <span className="font-mono text-[10px] text-slate-500">{c.product_count} parts</span>
                                <div className="flex gap-1">
                                    <button onClick={() => move(idx, idx - 1)} disabled={idx === 0} className="p-1 text-slate-600 hover:text-[hsl(var(--primary))] disabled:opacity-30" title="Lart" data-testid={`move-category-up-${c.id}`}>
                                        <ArrowLeft className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => move(idx, idx + 1)} disabled={idx === categories.length - 1} className="p-1 text-slate-600 hover:text-[hsl(var(--primary))] disabled:opacity-30" title="Poshtë" data-testid={`move-category-down-${c.id}`}>
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => setEditing({ id: c.id, name: c.name, image_url: c.image_url || "", order: c.order || 0 })} className="p-1 text-slate-600 hover:text-[hsl(var(--primary))]" data-testid={`edit-category-${c.id}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => setDimCat(c)} className={`p-1 hover:text-[hsl(var(--primary))] ${c.dim_search?.enabled ? "text-[hsl(var(--primary))]" : "text-slate-600"}`} title="Kërkimi me dimensione" data-testid={`dims-category-${c.id}`}>
                                        <Ruler className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => askRemove(c)} className="p-1 text-slate-600 hover:text-red-600" data-testid={`delete-category-${c.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white border border-slate-300 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="category-edit-modal">
                        <div className="px-4 py-3 border-b border-slate-200 flex justify-between">
                            <div className="font-display font-bold uppercase">{editing.id ? "Edit" : "New"} category</div>
                            <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div>
                                <label className="label-kicker mb-1 block">Name *</label>
                                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-field" data-testid="category-name-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Renditja (#)</label>
                                <input type="number" min="0" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="input-field" data-testid="category-order-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Image</label>
                                <ImageUploader
                                    value={editing.image_url}
                                    onChange={(v) => setEditing({ ...editing, image_url: v })}
                                    testId="category-image-uploader"
                                    recommended="640×480px · WEBP/JPG · ~80KB · square crop preferred"
                                />
                            </div>
                            {error && <div className="text-sm text-red-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>}
                        </div>
                        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
                            <button onClick={save} disabled={!editing.name} className="btn-primary" data-testid="save-category-button">
                                <Check className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!confirm}
                title={confirm?.title}
                message={confirm?.message}
                confirmText="Delete"
                destructive
                onConfirm={() => confirm?.onConfirm()}
                onCancel={() => setConfirm(null)}
            />
        </div>
    );
}
